import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { ISiteNameWithId } from '~src/siteManagement/serverApi/types/ISiteNameWithId';
import { SiteNameWithIds } from '~src/siteManagement/services/resources/SiteNameWithIds';
import { SiteMembershipDialogFilters } from '~src/userManagement/components/SiteRolesPickerContainer';
import { ServerUsers } from '~src/userManagement/services/resources/ServerUsers';
import { IGetUsersSiteRolesResult } from '~src/userManagement/users/serverApi/requests/GetUsersSiteRolesRequest';
import { ISiteRole } from '~src/userManagement/users/serverApi/types/ISiteRole';

import {
  AdminProfileUserSiteMembershipDialogBody,
  AdminProfileUserSiteMembershipDialogProps,
  AdminProfileUserSiteMembershipDialogState,
  NONE_PROFILE_VALUE,
} from '../components/AdminProfileUserSiteMembershipDialog';
import { SelectedSystemUser } from '../entityActions/AdminProfileRemoveFromUserAction';
import { ISiteAdminProfileAssignment } from '../requests/SetUserAdminProfileBySitesRequest';
import { AdminProfileSiteManagementDialogStyles } from '../styles/AdminProfileSiteManagementDialogStyles';
import { AdminProfilesService } from './AdminProfilesService';


/**
 * Builds profileBySiteId from the authoritative getUsersSiteRoles response.
 * Only includes sites the user is a member of (userSiteRoles is already filtered to those).
 * Uses ISiteRole.adminProfileLuid directly — no name matching needed.
 */
function buildProfileBySiteIdFromSiteRoles(
  memberSites: ISiteNameWithId[],
  userSiteRoles: ISiteRole[]
): Record<string, string> {
  const siteRoleMap = new Map(
    userSiteRoles.map((r) => [r.siteId, r.adminProfileLuid ?? NONE_PROFILE_VALUE])
  );
  const result: Record<string, string> = {};
  for (const site of memberSites) {
    result[site.id] = siteRoleMap.get(site.id) ?? NONE_PROFILE_VALUE;
  }
  return result;
}

export async function showAdminProfileUserSiteMembershipDialog(
  selectedUsers: SelectedSystemUser[],
  currentProfileId: string
): Promise<any> {
  const selectedUser = selectedUsers[0];

  const [sitesResult, availableProfiles, siteRolesResult] = await Promise.all([
    SliceableHelper.fetchAll(SiteNameWithIds.instance.all()) as Promise<ListResult<ISiteNameWithId>>,
    AdminProfilesService.getAdminProfilesForServer(),
    ServerUsers.instance.getSiteRoles([selectedUser.systemUserId ?? selectedUser.id]) as Promise<IGetUsersSiteRolesResult>,
  ]);

  const allSites = sitesResult.items;

  // The user's per-site role data — one ISiteRole entry per site they belong to.
  const userSiteRoles: ISiteRole[] = siteRolesResult.users?.[0]?.sites ?? [];

  // Only show rows for sites the user is actually a member of.
  const memberSiteIds = new Set(userSiteRoles.map((r) => r.siteId));
  const memberSites = allSites.filter((s) => memberSiteIds.has(s.id));

  // Build initial profile-per-site map from authoritative backend data.
  const profileBySiteId = buildProfileBySiteIdFromSiteRoles(memberSites, userSiteRoles);

  const selectedUserName = selectedUsers.length === 1
    ? selectedUser.name
    : `${selectedUsers.length} users`;

  const initialBodyProps: AdminProfileUserSiteMembershipDialogProps = {
    selectedUserName,
    currentProfileId,
    availableProfiles,
  };

  const initialDialogState: AdminProfileUserSiteMembershipDialogState = {
    sites: memberSites,
    profileBySiteId,
    loading: false,
    searchQuery: '',
    siteFilter: SiteMembershipDialogFilters.AllSites,
  };

  const modalInstance = openReactActionDialog<
    AdminProfileUserSiteMembershipDialogProps,
    AdminProfileUserSiteMembershipDialogState,
    any
  >({
    bodyComponent: AdminProfileUserSiteMembershipDialogBody,
    initialBodyProps,
    initialDialogState,
    title: Messages.adminProfileUserSiteMembershipDialogTitle(),
    actionName: Messages.save(),
    actionDisabled: (_bodyProps, dialogState) =>
      Object.keys(profileBySiteId).every(
        (siteId) => dialogState.profileBySiteId[siteId] === profileBySiteId[siteId]
      ),
    action: async (_bodyProps, dialogState) => {
      const assignments: ISiteAdminProfileAssignment[] = Object.keys(dialogState.profileBySiteId)
        .map((siteId) => ({
          siteId,
          adminProfileLuid: dialogState.profileBySiteId[siteId] === NONE_PROFILE_VALUE
            ? null
            : dialogState.profileBySiteId[siteId],
        }));

      await AdminProfilesService.setUserAdminProfileBySites({
        userId: selectedUser.systemUserId ?? selectedUser.id,
        assignments,
      });
    },
    dismissDialogOnActionFailed: false,
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width700px,
    dialogHeight: 500,
    dialogBodyStyle: AdminProfileSiteManagementDialogStyles.DialogBodyStyle,
  });

  return modalInstance.result;
}

export const AdminProfileUserSiteMembershipDialogAction = {
  showAdminProfileUserSiteMembershipDialog,
};
